import React, { useContext, useEffect, useMemo, useState } from "react";

import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Typography,
  TextField,
} from "@material-ui/core";

import {
  DeleteOutline,
  CloudUpload as UploadIcon,
  TextFields as TextIcon,
  Description as DocIcon,
  InsertDriveFile as FileIcon,
} from "@material-ui/icons";

import { makeStyles } from "@material-ui/core/styles";
import { fade } from "@material-ui/core/styles/colorManipulator";

import { toast } from "react-toastify";
import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import ConfirmationModal from "../../components/ConfirmationModal";
import { AuthContext } from "../../context/Auth/AuthContext";


const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(2),
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(2.5),
    overflow: "hidden",
  },

  /* Header actions */
  headerRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    flexWrap: "wrap",
    gap: theme.spacing(1.5),
  },
  headerInfo: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    minWidth: 0,
  },
  headerIcon: {
    width: 40,
    height: 40,
    borderRadius: 10,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: fade("#1976d2", 0.1),
    flexShrink: 0,
  },
  actionBtns: {
    display: "flex",
    gap: theme.spacing(1),
  },
  actionBtn: {
    borderRadius: 10,
    textTransform: "none",
    fontWeight: 500,
    padding: theme.spacing(0.8, 2),
  },

  /* Document cards */
  docGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
    gap: theme.spacing(2),
    maxWidth: "100%",
  },
  docCard: {
    borderRadius: 14,
    border: `1px solid ${theme.palette.divider}`,
    padding: theme.spacing(2),
    display: "flex",
    alignItems: "flex-start",
    gap: theme.spacing(1.5),
    transition: "box-shadow .2s, border-color .2s",
    overflow: "hidden",
    minWidth: 0,
    "&:hover": {
      boxShadow: `0 4px 16px ${fade(theme.palette.primary.main, 0.1)}`,
      borderColor: theme.palette.primary.light,
    },
  },
  docIconWrap: {
    width: 44,
    height: 44,
    borderRadius: 10,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  },
  docContent: {
    flex: 1,
    minWidth: 0,
  },
  docTitle: {
    fontWeight: 600,
    fontSize: "0.9rem",
    lineHeight: 1.3,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  docMeta: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    marginTop: theme.spacing(0.5),
    flexWrap: "wrap",
  },
  docDate: {
    fontSize: "0.75rem",
    color: theme.palette.text.disabled,
  },
  deleteBtn: {
    alignSelf: "flex-start",
    marginTop: -4,
    marginRight: -4,
    color: theme.palette.text.disabled,
    "&:hover": {
      color: theme.palette.error.main,
    },
  },

  /* Empty state */
  emptyState: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    padding: theme.spacing(6, 2),
    textAlign: "center",
    borderRadius: 14,
    border: `2px dashed ${theme.palette.divider}`,
    backgroundColor: fade(theme.palette.primary.main, 0.02),
  },
  emptyIcon: {
    fontSize: 48,
    color: theme.palette.text.disabled,
    marginBottom: theme.spacing(1),
  },

  /* Dialog improvements */
  dialogPaper: {
    borderRadius: 16,
  },
  dialogTitle: {
    fontWeight: 600,
  },
  dialogField: {
    "& .MuiOutlinedInput-root": {
      borderRadius: 10,
    },
  },
  dialogActions: {
    padding: theme.spacing(1.5, 3, 2),
  },
  dialogBtn: {
    borderRadius: 10,
    textTransform: "none",
    fontWeight: 500,
  },

  /* Upload area */
  uploadArea: {
    border: `2px dashed ${theme.palette.divider}`,
    borderRadius: 12,
    padding: theme.spacing(3),
    textAlign: "center",
    cursor: "pointer",
    transition: "border-color .2s, background .2s",
    "&:hover": {
      borderColor: theme.palette.primary.main,
      backgroundColor: fade(theme.palette.primary.main, 0.04),
    },
  },
  uploadFileName: {
    marginTop: theme.spacing(1),
    fontWeight: 500,
    color: theme.palette.primary.main,
  },
}));


const STATUS_COLORS = {
  indexed: "#43a047",
  stored: "#1976d2",
  processing: "#fb8c00",
  failed: "#e53935",
  pending: "#9e9e9e",
};

const TYPE_COLORS = {
  text: "#9c27b0",
  txt: "#9c27b0",
  pdf: "#e53935",
  docx: "#1976d2",
  csv: "#43a047",
  json: "#ff9800",
  file: "#607d8b",
};

const formatBytes = (bytes) => {
  const n = Number(bytes || 0);
  if (!n) return null;
  const units = ["B", "KB", "MB", "GB"];
  let i = 0;
  let v = n;
  while (v >= 1024 && i < units.length - 1) {
    v /= 1024;
    i += 1;
  }
  return `${v.toFixed(i === 0 ? 0 : 1)} ${units[i]}`;
};


const KnowledgeBasePanel = () => {
  const classes = useStyles();
  const { user } = useContext(AuthContext);

  const [loading, setLoading] = useState(false);
  const [documents, setDocuments] = useState([]);

  const [uploadOpen, setUploadOpen] = useState(false);
  const [textOpen, setTextOpen] = useState(false);

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [selectedDoc, setSelectedDoc] = useState(null);

  const [uploadForm, setUploadForm] = useState({
    title: "",
    description: "",
    file: null,
  });

  const [textForm, setTextForm] = useState({
    title: "",
    description: "",
    content: "",
  });

  const statusLabel = useMemo(() => {
    const s = i18n.t("prompts.knowledgeBase.status");
    return {
      indexed: s?.indexed || "Indexed",
      stored: s?.stored || "Stored",
      processing: s?.processing || "Processing",
      failed: s?.failed || "Failed",
      pending: s?.pending || "Pending",
    };
  }, []);

  const fetchDocs = async () => {
    try {
      setLoading(true);
      const { data } = await api.get("/ai/knowledge-documents");
      setDocuments(data?.documents || []);
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user?.companyId) {
      fetchDocs();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.companyId]);

  const handleUpload = async () => {
    if (!uploadForm.file) {
      toast.error(i18n.t("prompts.knowledgeBase.toasts.fileRequired"));
      return;
    }
    try {
      const fd = new FormData();
      fd.append("file", uploadForm.file);
      if (uploadForm.title) fd.append("title", uploadForm.title);
      if (uploadForm.description) fd.append("description", uploadForm.description);

      await api.post("/ai/knowledge-documents/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      toast.success(i18n.t("prompts.knowledgeBase.toasts.uploaded"));
      setUploadOpen(false);
      setUploadForm({ title: "", description: "", file: null });
      fetchDocs();
    } catch (err) {
      toastError(err);
    }
  };

  const handleCreateText = async () => {
    if (!String(textForm.title || "").trim()) {
      toast.error(i18n.t("prompts.knowledgeBase.toasts.titleRequired"));
      return;
    }
    if (!String(textForm.content || "").trim()) {
      toast.error(i18n.t("prompts.knowledgeBase.toasts.contentRequired"));
      return;
    }
    try {
      await api.post("/ai/knowledge-documents", {
        title: textForm.title,
        description: textForm.description,
        content: textForm.content,
      });
      toast.success(i18n.t("prompts.knowledgeBase.toasts.created"));
      setTextOpen(false);
      setTextForm({ title: "", description: "", content: "" });
      fetchDocs();
    } catch (err) {
      toastError(err);
    }
  };

  const handleDelete = async (docId) => {
    try {
      await api.delete(`/ai/knowledge-documents/${docId}`);
      toast.success(i18n.t("prompts.knowledgeBase.toasts.deleted"));
      setConfirmOpen(false);
      setSelectedDoc(null);
      fetchDocs();
    } catch (err) {
      toastError(err);
    }
  };

  return (
    <div className={classes.root}>
      <ConfirmationModal
        title={selectedDoc ? `${i18n.t("prompts.knowledgeBase.confirm.deleteTitle")} ${selectedDoc.title}?` : ""}
        open={confirmOpen}
        onClose={() => {
          setConfirmOpen(false);
          setSelectedDoc(null);
        }}
        onConfirm={() => selectedDoc && handleDelete(selectedDoc.id)}
      >
        {i18n.t("prompts.knowledgeBase.confirm.deleteMessage")}
      </ConfirmationModal>

      {/* Header */}
      <div className={classes.headerRow}>
        <div className={classes.headerInfo}>
          <div className={classes.headerIcon}>
            <DocIcon style={{ color: "#1976d2" }} />
          </div>
          <div>
            <Typography variant="subtitle1" style={{ fontWeight: 600 }}>
              Base de Conocimiento
            </Typography>
            <Typography variant="caption" color="textSecondary">
              {documents.length} documento{documents.length !== 1 ? "s" : ""} cargado{documents.length !== 1 ? "s" : ""}
            </Typography>
          </div>
        </div>
        <div className={classes.actionBtns}>
          <Button
            className={classes.actionBtn}
            variant="contained"
            color="primary"
            startIcon={<UploadIcon />}
            onClick={() => setUploadOpen(true)}
          >
            {i18n.t("prompts.knowledgeBase.buttons.upload")}
          </Button>
          <Button
            className={classes.actionBtn}
            variant="outlined"
            color="primary"
            startIcon={<TextIcon />}
            onClick={() => setTextOpen(true)}
          >
            {i18n.t("prompts.knowledgeBase.buttons.addText")}
          </Button>
        </div>
      </div>

      {/* Document cards */}
      {documents.length > 0 ? (
        <div className={classes.docGrid}>
          {documents.map((d) => {
            const typeColor = TYPE_COLORS[d.documentType] || TYPE_COLORS.file;
            const statusColor = STATUS_COLORS[d.status] || STATUS_COLORS.pending;
            const size = formatBytes(d.fileSize);

            return (
              <Paper key={d.id} className={classes.docCard} elevation={0}>
                <div
                  className={classes.docIconWrap}
                  style={{ backgroundColor: fade(typeColor, 0.1) }}
                >
                  <FileIcon style={{ color: typeColor }} />
                </div>
                <div className={classes.docContent}>
                  <Typography className={classes.docTitle}>
                    {d.fileUrl ? (
                      <a
                        href={d.fileUrl}
                        target="_blank"
                        rel="noreferrer"
                        style={{ color: "inherit", textDecoration: "none" }}
                      >
                        {d.title}
                      </a>
                    ) : (
                      d.title
                    )}
                  </Typography>
                  <div className={classes.docMeta}>
                    <Chip
                      label={d.documentType || "file"}
                      size="small"
                      style={{
                        height: 20,
                        fontSize: "0.65rem",
                        fontWeight: 600,
                        backgroundColor: fade(typeColor, 0.12),
                        color: typeColor,
                      }}
                    />
                    <Chip
                      label={statusLabel[d.status] || d.status || "Pending"}
                      size="small"
                      style={{
                        height: 20,
                        fontSize: "0.65rem",
                        fontWeight: 600,
                        backgroundColor: fade(statusColor, 0.12),
                        color: statusColor,
                      }}
                    />
                    {size && (
                      <Typography variant="caption" color="textSecondary">
                        {size}
                      </Typography>
                    )}
                  </div>
                  <Typography className={classes.docDate}>
                    {d.createdAt ? new Date(d.createdAt).toLocaleString() : ""}
                  </Typography>
                </div>
                <IconButton
                  className={classes.deleteBtn}
                  size="small"
                  onClick={() => {
                    setSelectedDoc(d);
                    setConfirmOpen(true);
                  }}
                >
                  <DeleteOutline fontSize="small" />
                </IconButton>
              </Paper>
            );
          })}
        </div>
      ) : (
        !loading && (
          <div className={classes.emptyState}>
            <DocIcon className={classes.emptyIcon} />
            <Typography variant="subtitle1" style={{ fontWeight: 500 }}>
              {i18n.t("prompts.knowledgeBase.empty")}
            </Typography>
            <Typography variant="body2" color="textSecondary" style={{ marginTop: 4, maxWidth: 360 }}>
              Sube archivos o agrega textos para que tu asistente de IA tenga contexto sobre tu negocio
            </Typography>
          </div>
        )
      )}

      {/* Upload dialog */}
      <Dialog
        open={uploadOpen}
        onClose={() => setUploadOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ className: classes.dialogPaper }}
      >
        <DialogTitle className={classes.dialogTitle}>
          {i18n.t("prompts.knowledgeBase.modalUpload.title")}
        </DialogTitle>
        <DialogContent dividers style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          <TextField
            className={classes.dialogField}
            label={i18n.t("prompts.knowledgeBase.modalUpload.form.title")}
            variant="outlined"
            margin="dense"
            fullWidth
            value={uploadForm.title}
            onChange={(e) => setUploadForm((p) => ({ ...p, title: e.target.value }))}
          />
          <TextField
            className={classes.dialogField}
            label={i18n.t("prompts.knowledgeBase.modalUpload.form.description")}
            variant="outlined"
            margin="dense"
            fullWidth
            value={uploadForm.description}
            onChange={(e) => setUploadForm((p) => ({ ...p, description: e.target.value }))}
          />
          <div
            className={classes.uploadArea}
            onClick={() => document.getElementById("kb-file-input")?.click()}
          >
            <UploadIcon style={{ fontSize: 36, color: "#9e9e9e" }} />
            <Typography variant="body2" color="textSecondary" style={{ marginTop: 4 }}>
              Haz clic para seleccionar un archivo
            </Typography>
            <Typography variant="caption" color="textSecondary">
              PDF, TXT, DOCX, CSV
            </Typography>
            {uploadForm.file && (
              <Typography className={classes.uploadFileName}>
                {uploadForm.file.name}
              </Typography>
            )}
            <input
              id="kb-file-input"
              type="file"
              hidden
              onChange={(e) => setUploadForm((p) => ({ ...p, file: e.target.files?.[0] || null }))}
            />
          </div>
        </DialogContent>
        <DialogActions className={classes.dialogActions}>
          <Button className={classes.dialogBtn} onClick={() => setUploadOpen(false)} variant="outlined">
            {i18n.t("prompts.knowledgeBase.modalUpload.buttons.cancel")}
          </Button>
          <Button className={classes.dialogBtn} onClick={handleUpload} color="primary" variant="contained">
            {i18n.t("prompts.knowledgeBase.modalUpload.buttons.send")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Text dialog */}
      <Dialog
        open={textOpen}
        onClose={() => setTextOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{ className: classes.dialogPaper }}
      >
        <DialogTitle className={classes.dialogTitle}>
          {i18n.t("prompts.knowledgeBase.modalText.title")}
        </DialogTitle>
        <DialogContent dividers style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          <TextField
            className={classes.dialogField}
            label={i18n.t("prompts.knowledgeBase.modalText.form.title")}
            variant="outlined"
            margin="dense"
            fullWidth
            value={textForm.title}
            onChange={(e) => setTextForm((p) => ({ ...p, title: e.target.value }))}
          />
          <TextField
            className={classes.dialogField}
            label={i18n.t("prompts.knowledgeBase.modalText.form.description")}
            variant="outlined"
            margin="dense"
            fullWidth
            value={textForm.description}
            onChange={(e) => setTextForm((p) => ({ ...p, description: e.target.value }))}
          />
          <TextField
            className={classes.dialogField}
            label={i18n.t("prompts.knowledgeBase.modalText.form.content")}
            variant="outlined"
            margin="dense"
            fullWidth
            multiline
            rows={10}
            value={textForm.content}
            onChange={(e) => setTextForm((p) => ({ ...p, content: e.target.value }))}
          />
        </DialogContent>
        <DialogActions className={classes.dialogActions}>
          <Button className={classes.dialogBtn} onClick={() => setTextOpen(false)} variant="outlined">
            {i18n.t("prompts.knowledgeBase.modalText.buttons.cancel")}
          </Button>
          <Button className={classes.dialogBtn} onClick={handleCreateText} color="primary" variant="contained">
            {i18n.t("prompts.knowledgeBase.modalText.buttons.save")}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default KnowledgeBasePanel;
